import localStore from '@/utils/local-store';
import { computed } from '@preact/signals';
import type { ReadonlySignal } from '@preact/signals';
import { deepSignal } from '@deepsignal/preact';
import TrackPlay from './trackers/trackPlay';
import TrackListening from './trackers/trackListening';
import PodcastPlayer from './audio';
import type { Episode, Podcast } from '~/types/podcast';
import http from '~/shared/http';
import { jsonRequestHeaders } from '~/csrf';
import { isPubliclyAuthenticated } from '~/publicAuth';

const trackProgress = (id: string, seek: number, progress: number) => {
    localStore.set(`p_${id}`, {
        s: seek,
        p: progress,
    });
};

let _syncTimeout: ReturnType<typeof setTimeout> | undefined;
let _pendingPayload: ProgressPayload | undefined;
let _hasSyncedCurrentEpisode = false;
const SYNC_DEBOUNCE_MS = 30_000;
const SYNC_FIRST_MS = 2_000;

interface ProgressPayload {
    episodeId?: number;
    itunesId: string;
    media: string;
    seek: number;
    progress: number;
}

function sendProgressToServer(payload: ProgressPayload): void {
    if (!isPubliclyAuthenticated()) return;

    const body: Record<string, unknown> = {
        seek_seconds: Math.floor(payload.seek),
        progress_percent: Math.round(payload.progress * 100) / 100,
    };

    if (payload.episodeId != null) {
        body.episode_id = payload.episodeId;
    } else {
        body.itunes_id = payload.itunesId;
        body.media = payload.media;
    }

    http.post('/me/episode-progress', body, {
        headers: jsonRequestHeaders(),
    }).catch((error) => {
        console.warn('Failed to sync episode progress:', error);
    });
}

function syncProgressToServer(payload: ProgressPayload): void {
    _pendingPayload = payload;

    if (_syncTimeout) {
        return;
    }

    const delay = _hasSyncedCurrentEpisode ? SYNC_DEBOUNCE_MS : SYNC_FIRST_MS;

    _syncTimeout = setTimeout(() => {
        _syncTimeout = undefined;
        _hasSyncedCurrentEpisode = true;
        if (_pendingPayload) {
            sendProgressToServer(_pendingPayload);
            _pendingPayload = undefined;
        }
    }, delay);
}

function flushProgressToServer(payload: ProgressPayload): void {
    if (_syncTimeout) {
        clearTimeout(_syncTimeout);
        _syncTimeout = undefined;
    }
    sendProgressToServer(payload);
    _pendingPayload = undefined;
}

type StorePlayer = {
    state: 'loading' | 'unloaded' | 'buffering' | 'idle' | 'loaded';
    showLoadingSpinner: boolean;
    playing: boolean;
    episode: Episode | null;
    podcast: Podcast | null;
    seek: number;
    volume: number;
    mute: boolean;
    duration: number;
    buffered: number;
    rate: number;
};

const data = deepSignal<StorePlayer>({
    state: 'unloaded',
    showLoadingSpinner: false,
    playing: false,
    episode: null,
    podcast: null,
    rate: 1,
    seek: 0,
    mute: false,
    volume: 1,
    duration: 0,
    buffered: 0,
});

export interface PlayerStore {
    data: typeof data;
    _bufferingLock: boolean;
    _isSeeking: boolean;
    _mounted: boolean;
    _loadingSpinnerShownAt?: number;
    _loadingSpinnerShowTimeout?: ReturnType<typeof setTimeout>;
    _loadingSpinnerHideTimeout?: ReturnType<typeof setTimeout>;
    _tracker?: TrackPlay;
    _listeningTracker?: TrackListening;
    _audio?: PodcastPlayer;
    setState: (state: StorePlayer['state']) => void;
    clearLoadingSpinnerTimeouts: () => void;
    syncLoadingSpinner: (isLoading: boolean) => void;
    destroy: () => void;
    resetPlayingState: () => void;
    mount: (episode: Episode, podcast: Podcast, seek?: number) => void;
    play: (episode?: Episode, podcast?: Podcast, seek?: number) => void;
    pause: () => void;
    volume: (level: number) => void;
    seek: (seek: number) => void;
    mute: () => void;
    unmute: () => void;
    rate: (rate: number) => void;
    progress: ReadonlySignal<number>;
    currentTime: ReadonlySignal<string>;
    remaining: ReadonlySignal<string>;
    getAudio: () => PodcastPlayer | undefined;
}

const LOADING_SPINNER_DELAY_MS = 120;
const LOADING_SPINNER_MIN_VISIBLE_MS = 700;

function setMedia(episode: Episode, podcast: Podcast) {
    const mediaSession = navigator.mediaSession;
    if (!mediaSession) return;

    mediaSession.metadata = new MediaMetadata({
        title: episode.title,
        artist: podcast.title,
        album: podcast.title,
        artwork: [
            {
                src: podcast.images.medium,
                sizes: '300x300',
                type: 'image/jpeg',
            },
        ],
    });

    mediaSession.setActionHandler('play', () => {
        player.play();
    });
    mediaSession.setActionHandler('pause', () => {
        player.pause();
    });
    mediaSession.setActionHandler('seekbackward', (details) => {
        player.seek(data.seek.value - (details.seekOffset ?? 15));
    });
    mediaSession.setActionHandler('seekforward', (details) => {
        player.seek(data.seek.value + (details.seekOffset ?? 15));
    });
    mediaSession.setActionHandler('seekto', (details) => {
        if (details.seekTime != null) {
            player.seek(details.seekTime);
        }
    });
}

function updatePositionState() {
    if (!navigator.mediaSession || !data.duration.value) return;
    try {
        navigator.mediaSession.setPositionState({
            duration: data.duration.value,
            playbackRate: data.rate.value,
            position: Math.min(data.seek.value, data.duration.value),
        });
    } catch {
        // Ignore errors from invalid state (e.g. duration is 0 or NaN)
    }
}

export const player: PlayerStore = {
    data,
    _bufferingLock: false,
    _isSeeking: false,
    _mounted: false,
    _loadingSpinnerShownAt: undefined,
    _loadingSpinnerShowTimeout: undefined,
    _loadingSpinnerHideTimeout: undefined,
    _tracker: undefined,
    _listeningTracker: undefined,
    _audio: undefined,
    setState(state: StorePlayer['state']) {
        this.data.state.value = state;
        this.syncLoadingSpinner(state === 'loading');
    },
    clearLoadingSpinnerTimeouts() {
        if (this._loadingSpinnerShowTimeout) {
            clearTimeout(this._loadingSpinnerShowTimeout);
            this._loadingSpinnerShowTimeout = undefined;
        }

        if (this._loadingSpinnerHideTimeout) {
            clearTimeout(this._loadingSpinnerHideTimeout);
            this._loadingSpinnerHideTimeout = undefined;
        }
    },
    syncLoadingSpinner(isLoading: boolean) {
        if (isLoading) {
            if (this._loadingSpinnerHideTimeout) {
                clearTimeout(this._loadingSpinnerHideTimeout);
                this._loadingSpinnerHideTimeout = undefined;
            }

            if (this.data.showLoadingSpinner.value || this._loadingSpinnerShowTimeout) {
                return;
            }

            this._loadingSpinnerShowTimeout = setTimeout(() => {
                this._loadingSpinnerShownAt = Date.now();
                this.data.showLoadingSpinner.value = true;
                this._loadingSpinnerShowTimeout = undefined;
            }, LOADING_SPINNER_DELAY_MS);

            return;
        }

        if (this._loadingSpinnerShowTimeout) {
            clearTimeout(this._loadingSpinnerShowTimeout);
            this._loadingSpinnerShowTimeout = undefined;
        }

        if (!this.data.showLoadingSpinner.value) {
            return;
        }

        const shownAt = this._loadingSpinnerShownAt;
        if (shownAt == null) {
            console.assert(false, 'Loading spinner timestamp is missing while spinner is visible');
        }

        const elapsed = Date.now() - (shownAt ?? Date.now());
        const remaining = LOADING_SPINNER_MIN_VISIBLE_MS - elapsed;

        if (remaining <= 0) {
            this.clearLoadingSpinnerTimeouts();
            this.data.showLoadingSpinner.value = false;
            this._loadingSpinnerShownAt = undefined;
            return;
        }

        if (this._loadingSpinnerHideTimeout) {
            clearTimeout(this._loadingSpinnerHideTimeout);
        }

        this._loadingSpinnerHideTimeout = setTimeout(() => {
            this.data.showLoadingSpinner.value = false;
            this._loadingSpinnerShownAt = undefined;
            this._loadingSpinnerHideTimeout = undefined;
        }, remaining);
    },
    destroy() {
        this.clearLoadingSpinnerTimeouts();
        this.data.showLoadingSpinner.value = false;
        this.data.playing.value = false;
        this._loadingSpinnerShownAt = undefined;
        this._bufferingLock = false;

        if (this._audio) {
            this._audio.destroy();
            this._audio = undefined;
        }

        this._listeningTracker?.destroy();
        this._listeningTracker = undefined;
        this._tracker?.destroy();
        this._tracker = undefined;
        this._mounted = false;
    },
    resetPlayingState() {
        _hasSyncedCurrentEpisode = false;
        _pendingPayload = undefined;
        if (_syncTimeout) {
            clearTimeout(_syncTimeout);
            _syncTimeout = undefined;
        }
        this.setState('unloaded');
        this.data.seek.value = 0;
        this.data.duration.value = 0;
        this.data.buffered.value = 0;
        this.data.rate.value = 1;
    },
    mount(episode: Episode, podcast: Podcast, seek?: number) {
        // todo: Create a better type for playing episode.
        // Merge with podcasts and what we get from the API

        this._audio = new PodcastPlayer(episode);
        this._tracker = new TrackPlay(episode.media, podcast.itunes_id);
        this._listeningTracker = new TrackListening();
        this.data.podcast.value = podcast;
        this.data.episode.value = episode;
        this.setState('unloaded');
        this.data.duration.value = this._audio.duration();

        if (seek != null && seek > 0) {
            this.seek(seek);
        }

        this._audio.on('timeupdate', () => {
            this._bufferingLock = false;
            if (this._audio && !this._isSeeking) {
                const seek = this._audio.seek();
                this.data.seek.value = seek;
            }
            //todo: move this tracking into signal effect
            const itunesId = this.data.podcast.value?.itunes_id;
            const media = this.data.episode.value?.media;
            trackProgress(
                `${itunesId}_${media}`,
                this.data.seek.value,
                this.progress.value,
            );
            if (itunesId && media) {
                syncProgressToServer({
                    episodeId: this.data.episode.value?.id,
                    itunesId,
                    media,
                    seek: this.data.seek.value,
                    progress: this.progress.value,
                });
            }
            updatePositionState();
        });
        this._audio.on('ended', () => this._listeningTracker?.pause());
        this._audio.on('pause', () => {
            this._listeningTracker?.pause();
            this.data.playing.value = false;
            const itunesId = this.data.podcast.value?.itunes_id;
            const media = this.data.episode.value?.media;
            if (itunesId && media) {
                flushProgressToServer({
                    episodeId: this.data.episode.value?.id,
                    itunesId,
                    media,
                    seek: this.data.seek.value,
                    progress: this.progress.value,
                });
            }
        });
        this._audio.on('play', () => {
            this.data.playing.value = true;
        });
        this._audio.on('playing', () => {
            this._listeningTracker?.start();
            this._bufferingLock = false;
            // logNetworkState(this._controller.networkState());
            // logReadyState(this._controller.readyState());
            this.data.playing.value = true;
        });
        this._audio.on('loadedmetadata', () => {
            if (this._audio) {
                this.data.duration.value = this._audio.duration();
            }
            this._bufferingLock = false;
            this.setState('loaded');
            updatePositionState();
        });
        this._audio.on('seeked', () => {
            this._bufferingLock = false;
            this._isSeeking = false;
            this.setState('loaded');
        });
        this._audio.on('seeking', () => {
            this._listeningTracker?.pause();
            this._bufferingLock = true;
            // this.data.state.value = 'buffering';
            //todo: move this tracking into signal effect
            trackProgress(
                `${this.data.podcast.value?.itunes_id}_${this.data.episode.value?.media}`,
                this.data.seek.value,
                this.progress.value,
            );
            setTimeout(() => {
                if (this._bufferingLock && this.data.playing.value === true) {
                    this.setState('loading');
                }
            }, 300);
        });
        this._audio.on('stalled', () => {
            // For some reason safari gets stalled event even after canplaythrough
            // has occurred. Cant find any information about this
            // So we are just going to ignore this event for now
            //
            // this._bufferingLock = true;
            // setTimeout(() => {
            //   if (this._bufferingLock) {
            //     this.data.state.value = 'loading';
            //   }
            // }, 300);
        });
        this._audio.on('waiting', () => {
            this._listeningTracker?.pause();
            this._bufferingLock = true;
            setTimeout(() => {
                if (this._bufferingLock) {
                    this.setState('loading');
                }
            }, 300);
        });
        this._audio.on('canplay', () => {
            this._bufferingLock = false;
            this.setState('loaded');
        });
        this._audio.on('canplaythrough', () => {
            this._bufferingLock = false;
            this.setState('loaded');
        });
    },
    play(episode?: Episode, podcast?: Podcast, seek?: number) {
        if (episode) {
            this.destroy();
            this.resetPlayingState();
            this.data.episode.value = episode;
        } else if (!this._audio) {
            throw new Error('No episode to play');
        }

        if (!this._mounted && episode && podcast) {
            this.mount(episode, podcast, seek);
            this._mounted = true;
        }
        if (seek != null) {
            this._audio?.seek(seek);
        }

        this._audio
            ?.play()
            .then(() => {
                this._tracker?.start();

                if (this._audio) {
                    this.data.duration.value = this._audio.duration();
                }

                if (this.data.episode.value && this.data.podcast.value) {
                    setMedia(this.data.episode.value, this.data.podcast.value);
                }
            })
            .catch((error) => {
                // Ignore AbortError when pause() interrupts play()
                if (error.name !== 'AbortError') {
                    console.error('Play error:', error);
                }
            });
    },
    pause() {
        const audio = this.getAudio();
        if (audio) {
            this._tracker?.pause();
            audio.pause();
        }
    },
    seek(seek: number) {
        const audio = this.getAudio();
        if (audio) {
            this._isSeeking = true;
            this.data.seek.value = audio.seek(seek);
            updatePositionState();
        }
    },
    volume(level: number) {
        const audio = this.getAudio();
        if (audio) {
            this.data.volume.value = audio.volume(level);
        }
    },
    mute() {
        const audio = this.getAudio();
        if (audio) {
            this.data.mute.value = true;
            audio.mute();
        }
    },
    unmute() {
        const audio = this.getAudio();
        if (audio) {
            this.data.mute.value = false;
            audio.unmute();
        }
    },
    rate(rate: number) {
        const audio = this.getAudio();
        if (audio) {
            this.data.rate.value = audio.rate(rate);
        }
    },
    progress: computed(() => {
        if (!data.duration.value) return 0;
        return (data.seek.value / data.duration.value) * 100;
    }),
    currentTime: computed(() => {
        return formatSecondsToTime(data.seek.value);
    }),
    remaining: computed(() => {
        return formatSecondsToTime(data.duration.value - data.seek.value);
    }),
    getAudio() {
        return this._audio;
    },
};

export const getProgress = (episode: { media: string }, podcastId: string) => {
    const data = localStore.get<{ p: number; s: number }>(`p_${podcastId}_${episode.media}`);
    const progress = { progress: 0, currentSeek: 0 };
    if (data) {
        progress.progress = data.p;
        progress.currentSeek = data.s;
    }
    return progress;
};

function formatSecondsToTime(seconds: number) {
    if (seconds <= 0) return '00:00';
    let hours = 0;
    let minutes = 0;
    let sec = 0;

    hours = Math.floor(seconds / (60 * 60));
    minutes = Math.floor((seconds / 60) % 60);
    sec = Math.floor(seconds % 60);

    const time = [];
    // check if leading zero is need
    if (hours > 0 && hours < 10) time.push(`0${hours}`);
    time.push(minutes < 10 ? `0${minutes}` : minutes);
    time.push(sec < 10 ? `0${sec}` : sec);

    return time.join(':');
}
